import React, { useState } from 'react';

import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';

import { Grid, GridColumn } from '@atlaskit/page';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import Sidebar from 'src/app/sidebar';
import { FocusedPageHeader } from 'src/components/accessibility';
import { DiffLoader } from 'src/components/diff-set/diff-loader';
import { DiffProvider } from 'src/components/diff-set/diff-provider';
import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import { useFetchConflicts } from 'src/components/diff-set/hooks/conflicts';
import { useDiffSetUrls } from 'src/components/diff-set/urls';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { BRANCH_LOAD, MetricSender } from 'src/metrics';
import {
  getBranchViewSourceBranchHash,
  getBranchViewDestinationBranchHash,
  getBranchViewDestinationRepoName,
  getBranchViewDestinationBranchName,
} from 'src/redux/repository/branch/branch-redux';
import RepositoryCommitList from 'src/sections/repository/components/commit-list';
import messages from 'src/sections/repository/components/commit-list/commit-list.i18n';
import * as commitStyles from 'src/sections/repository/components/commit-list/commit-list.style';
import { REPO_EVENT_SOURCES } from 'src/sections/repository/types';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';

import { useFetchBranchCommits } from '../../../components/commit-list/hooks/use-branch-commit';
import { useBranchCommitListDetails } from '../hooks/branch-commit-list';
import { useFetchBranchPrList } from '../hooks/branch-pr-list';
import { useBranchSyncInfoDetails } from '../hooks/branch-sync-info';
import {
  useFetchDetailedBranchInfo,
  useUnloadBranch,
} from '../hooks/use-branch';
import { useFetchBranchSyncInfo } from '../hooks/use-branch-sync-info';

import { BranchBreadcrumbs } from './branch-breadcrumbs';
import { BranchDialogZone } from './branch-dialog-zone';
import { messages as branchMessages } from './branch-page.i18n';
import { CompareWidget } from './compare-widget';
import { EmptyBranch } from './empty-branch';
import { HeaderActions } from './header-actions/header-actions';
import BranchSidebar from './sidebar';

export const BranchPage = ({
  branchName,
}: {
  branchName: string;
}): JSX.Element => {
  const [isDiffLoaded, setIsDiffLoaded] = useState(false);
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug) || '';
  const sourceHash = useSelector(getBranchViewSourceBranchHash) || '';
  const destinationHash = useSelector(getBranchViewDestinationBranchHash) || '';
  const destRepoName = useSelector(getBranchViewDestinationRepoName) || '';
  const destBranchName = useSelector(getBranchViewDestinationBranchName) || '';
  const onDiffLoaded = () => setIsDiffLoaded(true);

  const intl = useIntl();

  const { diffUrlBuilder, diffStatUrlBuilder } = useDiffSetUrls(
    repositoryFullSlug,
    sourceHash,
    destRepoName,
    destinationHash
  );
  useFetchConflicts(sourceHash, destinationHash, destRepoName);

  const diffMetadataProps: DiffMetadataProps = {
    sourceHash,
    destinationHash,
    sourceRepoFullSlug: repositoryFullSlug,
    destinationRepoFullSlug: destRepoName,
  };

  useFetchBranchCommits(sourceHash, destinationHash, destRepoName);
  useFetchBranchSyncInfo(sourceHash, destinationHash);
  useFetchDetailedBranchInfo();
  useUnloadBranch();
  const branchSyncInfoDetails = useBranchSyncInfoDetails();

  const branchPrList = useFetchBranchPrList(branchName, destBranchName);
  const { isLoading: isCommitListLoading } = useBranchCommitListDetails();
  const commitListDetails = useBranchCommitListDetails();

  const tabTitleChangesEnabled = useStatsigGate(
    StatsigFeatureKeys.tabTitleChangesEnabled
  );

  return (
    <>
      <Helmet
        title={
          tabTitleChangesEnabled
            ? `${intl.formatMessage(branchMessages.branch)} ${branchName}`
            : `/ ${intl.formatMessage(branchMessages.branch)} ${branchName}`
        }
        defer={false}
      />
      <Grid layout="fluid" spacing="comfortable">
        <GridColumn>
          <FocusedPageHeader breadcrumbs={<BranchBreadcrumbs />}>
            {branchName}
          </FocusedPageHeader>
          <HeaderActions branchName={branchName} branchPrList={branchPrList} />

          <CompareWidget
            branchName={branchName}
            repositoryFullSlug={repositoryFullSlug}
          />

          <commitStyles.CommitList
            data-qa="commit-list-styles"
            aria-label={intl.formatMessage(messages.commitListLabel)}
          >
            <RepositoryCommitList
              analyticsParentPage={REPO_EVENT_SOURCES.BRANCH_SCREEN}
              repositoryFullSlug={repositoryFullSlug}
              commitListDetails={commitListDetails}
              branchSyncInfoDetails={branchSyncInfoDetails}
              startExpanded
            />
          </commitStyles.CommitList>
          {!isCommitListLoading && isDiffLoaded && (
            <MetricSender metric={BRANCH_LOAD} />
          )}
          <DiffProvider
            diffUrlBuilder={diffUrlBuilder}
            diffStatUrlBuilder={diffStatUrlBuilder}
            diffMetadataProps={diffMetadataProps}
            maxDiffStatPages={1}
          >
            <DiffLoader
              NoDiffComponent={<EmptyBranch branchName={branchName} />}
              onDiffLoaded={onDiffLoaded}
            />
          </DiffProvider>
        </GridColumn>
        <BranchDialogZone
          branchName={branchName}
          diffMetadataProps={diffMetadataProps}
        />
        <Sidebar>
          <BranchSidebar branchPrList={branchPrList} />
        </Sidebar>
      </Grid>
    </>
  );
};
